import { FetchStatus } from '../types/common';

// =====================================================================
// Store d'authentification — état CLIENT (le navigateur en est la source
// de vérité : token, refreshToken, isAuthenticated, etc.).
//
// Implémentation : petit store observable « vanilla » (sans dépendance),
// aligné sur SITES_WEB/app_mystic/src/store/authStore.ts — persisté dans
// localStorage pour survivre au F5 (auparavant un slice Redux purement en
// mémoire, jamais persisté). Les 3 autres domaines (appData/userData/
// gitlabAdmin) restent Redux, non concernés par ce changement.
//  - Côté React : lu via useSyncExternalStore (cf. hooks/useAuthState.ts).
//  - Côté non-React : authService l'importe directement, sans injection.
// =====================================================================

interface User {
  id: number;
  login: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  status: FetchStatus;
  errorMessage: string;
  isAuthenticated: boolean;
  isRefreshing: boolean;
}

const STORAGE_KEY = 'pwa_dashboard.auth';

// Échéance (ms epoch) lue dans le champ « exp » du JWT : l'API ne renvoie pas de durée
// (expires_in) et elle diffère selon la route (24 h au login, 15 min après /api/refresh).
// Sans elle, ni l'auto-refresh de useAuth ni getValidToken() ne se déclenchent.
export function tokenExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function expiresAt(token: string, expiresIn?: number): number | null {
  return expiresIn ? Date.now() + expiresIn * 1000 : tokenExpiry(token);
}

const defaultState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  tokenExpiresAt: null,
  status: FetchStatus.INITIAL,
  errorMessage: '',
  isAuthenticated: false,
  isRefreshing: false,
};

// Champs réellement persistés (connexion durable). On exclut le transitoire :
// status, errorMessage et isRefreshing sont recalculés à chaque session.
type PersistedAuth = Pick<
  AuthState,
  'user' | 'token' | 'refreshToken' | 'tokenExpiresAt' | 'isAuthenticated'
>;

// Hydrate l'état initial depuis localStorage → la connexion survit au F5.
function loadInitialState(): AuthState {
  if (typeof window === 'undefined') return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const persisted = JSON.parse(raw) as PersistedAuth;
    return {
      ...defaultState,
      ...persisted,
      // Sessions enregistrées avant le calcul de l'échéance : on la retrouve dans le jeton.
      tokenExpiresAt: persisted.tokenExpiresAt ?? tokenExpiry(persisted.token),
      status: persisted.isAuthenticated ? FetchStatus.SUCCESS : FetchStatus.INITIAL,
    };
  } catch {
    return defaultState;
  }
}

function persist(s: AuthState) {
  if (typeof window === 'undefined') return;
  try {
    if (s.isAuthenticated && s.token) {
      const data: PersistedAuth = {
        user: s.user,
        token: s.token,
        refreshToken: s.refreshToken,
        tokenExpiresAt: s.tokenExpiresAt,
        isAuthenticated: s.isAuthenticated,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      // logout / échec → on purge le stockage.
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage indisponible (navigation privée, quota) : on ignore.
  }
}

let state: AuthState = loadInitialState();
const listeners = new Set<() => void>();

function setState(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  persist(state);
  listeners.forEach((listener) => listener());
}

export const authStore = {
  getState: (): AuthState => state,

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  // --- Actions (équivalent des anciens reducers du slice Redux) ------

  loginStart() {
    setState({ status: FetchStatus.LOADING, errorMessage: '' });
  },

  loginMfaStart() {
    setState({ status: FetchStatus.MFA_LOADING, errorMessage: '' });
  },

  setMfaRequired() {
    setState({ status: FetchStatus.MFA_REQUIRED, errorMessage: '' });
  },

  loginSuccess(payload: {
    user: User;
    token: string;
    refreshToken: string;
    expiresIn?: number;
  }) {
    setState({
      status: FetchStatus.SUCCESS,
      user: { id: payload.user.id, login: payload.user.login },
      token: payload.token,
      refreshToken: payload.refreshToken,
      tokenExpiresAt: expiresAt(payload.token, payload.expiresIn),
      isAuthenticated: true,
      isRefreshing: false,
      errorMessage: '',
    });
  },

  loginFailure(message: string) {
    setState({
      status: FetchStatus.ERROR,
      errorMessage: message,
      user: null,
      token: null,
      isAuthenticated: false,
    });
  },

  logout() {
    setState({ ...defaultState });
  },

  clearError() {
    setState({ errorMessage: '', status: FetchStatus.INITIAL });
  },

  refreshTokenStart() {
    setState({ isRefreshing: true, errorMessage: '' });
  },

  refreshTokenSuccess(payload: { token: string; refreshToken?: string; expiresIn?: number }) {
    setState({
      token: payload.token,
      ...(payload.refreshToken ? { refreshToken: payload.refreshToken } : {}),
      tokenExpiresAt: expiresAt(payload.token, payload.expiresIn),
      isRefreshing: false,
      errorMessage: '',
    });
  },

  // Refresh interrompu par une erreur réseau : la session est conservée.
  refreshTokenAborted() {
    setState({ isRefreshing: false });
  },

  refreshTokenFailure(message: string) {
    setState({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isRefreshing: false,
      status: FetchStatus.ERROR,
      errorMessage: message,
      user: null,
    });
  },
};
